import React, { useState, useEffect, useMemo } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DatetimeRangePicker from "../common/DatetimeRangePicker";
import BatteryIcon from "../common/BatteryIcon";
import StatusTimeline from "./StatusTimeline";
import { locationsData } from "../../utils/constants";
import { queryData } from "../../utils/api";

// Status icon component with battery-style hover
function StatusIcon({ status, tooltip }) {
  let icon;
  if (status === 'online') {
    icon = <CheckCircleRoundedIcon className="text-green-600" />;
  } else if (status === 'warning') {
    icon = <WarningAmberRoundedIcon className="text-yellow-600" />;
  } else {
    icon = <ErrorOutlineRoundedIcon style={{ color: '#b90501' }} />;
  }
  
  return (
    <Tooltip 
      title={tooltip} 
      arrow
      componentsProps={{ 
        tooltip: { 
          sx: { background: '#000' } 
        } 
      }}
      placement="top"
    >
      {icon}
    </Tooltip>
  );
}

// Completeness badge component with battery-style hover
function CompletenessBadge({ percentage, tooltip }) {
  let color = 'bg-green-100 text-green-800';
  let icon = '🟢';
  
  if (percentage < 50) {
    color = 'bg-red-100 text-red-800';
    icon = '🔴';
  } else if (percentage < 95) {
    color = 'bg-yellow-100 text-yellow-800';
    icon = '🟠';
  }
  
  return (
    <Tooltip 
      title={tooltip} 
      arrow
      componentsProps={{ 
        tooltip: { 
          sx: { background: '#000' } 
        } 
      }}
      placement="top"
    >
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${color} cursor-help`}>
        <span>{icon}</span>
        <span>{percentage.toFixed(1)}%</span>
      </span>
    </Tooltip>
  );
}

// Hover cell component with battery-style tooltip
function HoverCell({ children, tooltip }) {
  return (
    <Tooltip 
      title={tooltip} 
      arrow
      componentsProps={{ 
        tooltip: { 
          sx: { background: '#000' } 
        } 
      }}
      placement="top"
    >
      <span className="cursor-help">{children}</span>
    </Tooltip>
  );
}

// Export section as CSV
function exportSectionCSV(locationName, devices, dateRange, showVariableStatus) {
  const headers = [
    'Location',
    'Device Serial',
    'Device Type',
    'Site/Species',
    'Status',
    'Completeness %',
    'Records',
    'Expected',
    'Missed',
    'Last Seen',
    'Battery Voltage'
  ];
  
  if (showVariableStatus) {
    headers.push('Variable', 'Variable Status', 'Variable Records');
  }
  
  const rows = [];
  
  devices.forEach(device => {
    if (showVariableStatus && device.variables) {
      // Export each variable as a separate row
      device.variables.forEach(variable => {
        rows.push([
          locationName,
          device.serialNumber,
          device.deviceType,
          ['MFR Node', 'WXT536 Sensor', 'AQT530 Sensor'].includes(device.deviceType) ? device.site : device.treeSpecies,
          device.status,
          device.dataCompleteness.toFixed(1),
          device.totalRecords,
          device.expectedPoints,
          device.missedSubmissions,
          device.lastSeen,
          device.batteryVoltage,
          variable.name,
          variable.status,
          variable.recordCount
        ]);
      });
    } else {
      rows.push([
        locationName,
        device.serialNumber,
        device.deviceType,
        ['MFR Node', 'WXT536 Sensor', 'AQT530 Sensor'].includes(device.deviceType) ? device.site : device.treeSpecies,
        device.status,
        device.dataCompleteness.toFixed(1),
        device.totalRecords,
        device.expectedPoints,
        device.missedSubmissions,
        device.lastSeen,
        device.batteryVoltage
      ]);
    }
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(v => `"${v}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const startDate = new Date(dateRange[0]).toISOString().split('T')[0];
  const endDate = new Date(dateRange[1]).toISOString().split('T')[0];
  a.download = `crocus-status-${locationName}-${startDate}-to-${endDate}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

// Export full report as HTML
function exportFullReportHTML(groupedData, dateRange, summary, timezone, showVariableStatus) {
  const startDate = new Date(dateRange[0]).toLocaleString(undefined, { timeZone: timezone });
  const endDate = new Date(dateRange[1]).toLocaleString(undefined, { timeZone: timezone });
  
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>CROCUS Status Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #5b21b6; }
    h2 { color: #7c3aed; margin-top: 30px; }
    h3 { color: #8b5cf6; margin-top: 20px; margin-bottom: 10px; }
    .summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 15px; margin: 20px 0; }
    .summary-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
    .summary-card h3 { margin: 0 0 10px 0; font-size: 14px; color: #666; }
    .summary-card .value { font-size: 28px; font-weight: bold; }
    .green { color: #16a34a; }
    .yellow { color: #ca8a04; }
    .red { color: #dc2626; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #5b21b6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
    .status-online { color: #16a34a; }
    .status-warning { color: #ca8a04; }
    .status-offline { color: #dc2626; }
    .completeness-good { background-color: #dcfce7; color: #166534; padding: 2px 8px; border-radius: 4px; }
    .completeness-warning { background-color: #fef9c3; color: #854d0e; padding: 2px 8px; border-radius: 4px; }
    .completeness-bad { background-color: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; }
    .metadata { margin: 20px 0; padding: 15px; background-color: #f9fafb; border-radius: 8px; }
    .variable-row { background-color: #f3f4f6; font-size: 0.9em; }
    .device-type-section { margin-top: 15px; }
  </style>
</head>
<body>
  <h1>CROCUS Status Report</h1>
  
  <div class="metadata">
    <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
    <p><strong>Date Range:</strong> ${startDate} to ${endDate}</p>
    <p><strong>Timezone:</strong> ${timezone}</p>
    <p><strong>Variable Status:</strong> ${showVariableStatus ? 'Enabled' : 'Disabled'}</p>
  </div>
  
  <h2>Summary Statistics</h2>
  <div class="summary">
    <div class="summary-card">
      <h3>Total Devices</h3>
      <div class="value">${summary.total}</div>
    </div>
    <div class="summary-card">
      <h3>Online</h3>
      <div class="value green">${summary.online}</div>
    </div>
    <div class="summary-card">
      <h3>Warning</h3>
      <div class="value yellow">${summary.warning}</div>
    </div>
    <div class="summary-card">
      <h3>Offline</h3>
      <div class="value red">${summary.offline}</div>
    </div>
    <div class="summary-card">
      <h3>Low Completeness</h3>
      <div class="value red">${summary.lowCompleteness}</div>
    </div>
  </div>
  
  ${groupedData.map(location => {
    // Group devices by type
    const devicesByType = {
      'MFR Node': [],
      'Sap Flow Sensor': [],
      'Band Dendrometer': [],
      'WXT536 Sensor': [],
      'AQT530 Sensor': []
    };
    
    location.devices.forEach(device => {
      if (devicesByType[device.deviceType]) {
        devicesByType[device.deviceType].push(device);
      }
    });
    
    return `
    <h2>${location.locationName} (${location.locationAbbrev})</h2>
    
    ${Object.entries(devicesByType).map(([deviceType, devices]) => {
      if (devices.length === 0) return '';
      
      const pluralType = deviceType === 'MFR Node' ? 'MFR Nodes' : 
                         deviceType === 'Sap Flow Sensor' ? 'Sap Flow Sensors' : 
                         deviceType === 'Band Dendrometer' ? 'Band Dendrometers' :
                         deviceType === 'WXT536 Sensor' ? 'WXT536 Weather Sensors' :
                         'AQT530 Air Quality Sensors';
      
      return `
      <div class="device-type-section">
        <h3>${pluralType}</h3>
        <table>
          <thead>
            <tr>
              <th>Serial</th>
              <th>Description</th>
              <th>Status</th>
              <th>Completeness</th>
              <th>Records</th>
              <th>Expected</th>
              <th>Missed</th>
              <th>Last Seen</th>
              <th>Battery</th>
            </tr>
          </thead>
          <tbody>
            ${devices.map(device => {
              const statusClass = device.status === 'online' ? 'status-online' : 
                                device.status === 'warning' ? 'status-warning' : 'status-offline';
              const completenessClass = device.dataCompleteness >= 95 ? 'completeness-good' :
                                       device.dataCompleteness >= 50 ? 'completeness-warning' : 'completeness-bad';
              
              let variableRows = '';
              if (showVariableStatus && device.variables) {
                variableRows = device.variables.map(variable => `
                  <tr class="variable-row">
                    <td colspan="2" style="padding-left: 30px;">└─ ${variable.name}</td>
                    <td class="${variable.status === 'online' ? 'status-online' : 'status-offline'}">${variable.status}</td>
                    <td colspan="5">${variable.recordCount} records</td>
                    <td></td>
                  </tr>
                `).join('');
              }
              
              const description = deviceType === 'MFR Node' ? device.site : 
                                 deviceType === 'WXT536 Sensor' ? device.site :
                                 deviceType === 'AQT530 Sensor' ? device.site :
                                 `${device.treeSpecies} (${device.treeDiameter})`;
              
              return `
              <tr>
                <td>${device.serialNumber}</td>
                <td>${description}</td>
                <td class="${statusClass}">${device.status}</td>
                <td><span class="${completenessClass}">${device.dataCompleteness.toFixed(1)}%</span></td>
                <td>${device.totalRecords}</td>
                <td>${device.expectedPoints}</td>
                <td>${device.missedSubmissions}</td>
                <td>${device.lastSeen}</td>
                <td>${device.batteryVoltage}V</td>
              </tr>
              ${variableRows}
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
      `;
    }).join('')}
    `;
  }).join('')}
  
  <div class="metadata" style="margin-top: 40px;">
    <p><strong>About This Report:</strong></p>
    <p>This status report was generated by the CROCUS Portal (portal.crocus-urban.org)</p>
    <p>CROCUS - Researching how extreme weather conditions impact the Chicago region and its people.</p>
    <p>Funded by the U.S. Department of Energy's Office of Science.</p>
  </div>
</body>
</html>`;
  
  const blob = new Blob([html], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const startDateStr = new Date(dateRange[0]).toISOString().split('T')[0];
  const endDateStr = new Date(dateRange[1]).toISOString().split('T')[0];
  a.download = `crocus-status-report-${startDateStr}-to-${endDateStr}.html`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function StatusReportPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusData, setStatusData] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [timezone, setTimezone] = useState(() => localStorage.getItem('crocus_timezone') || 'UTC');
  
  // Date range state - default to last 24 hours
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    return [start, end];
  });
  
  const [quickRange, setQuickRange] = useState('-24h');
  
  // Location filter state - single selection dropdown
  const [selectedLocation, setSelectedLocation] = useState('all');
  
  // Device type filter state - single selection dropdown
  const [selectedDeviceType, setSelectedDeviceType] = useState('all');
  
  // Show variable status checkbox
  const [showVariableStatus, setShowVariableStatus] = useState(false);
  
  // Show data timeline checkbox (like "Last X days of data" from location page)
  const [showDataTimeline, setShowDataTimeline] = useState(false);
  
  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Listen for timezone changes
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  // Get expected interval for device type (in seconds)
  const getExpectedInterval = (deviceType) => {
    if (deviceType === 'Band Dendrometer') {
      return 15 * 60; // 15 minutes
    } else if (deviceType === 'MFR Node') {
      return 30 * 60; // 30 minutes
    } else if (deviceType === 'Sap Flow Sensor') {
      return 20 * 60; // Average of 15-30 minutes
    } else if (deviceType === 'WXT536 Sensor') {
      return 10 * 60; // 10 minutes
    } else if (deviceType === 'AQT530 Sensor') {
      return 10 * 60; // 10 minutes
    }
    return 30 * 60; // Default
  };
  
  // Calculate expected data points based on device type, variables, and time range
  const calculateExpectedPoints = (deviceType, startTime, endTime, variableCount) => {
    const durationSeconds = (endTime - startTime) / 1000;
    const intervalSeconds = getExpectedInterval(deviceType);
    const submissionsExpected = Math.floor(durationSeconds / intervalSeconds);
    
    // Expected points = submissions × number of variables
    return submissionsExpected * variableCount;
  };
  
  // Fetch status data
  const fetchStatusData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [startTime, endTime] = dateRange;
      const startISO = startTime.toISOString();
      const endISO = endTime.toISOString();
      
      const allDeviceData = [];
      
      // Filter locations based on selection
      const locationsToQuery = selectedLocation === 'all' 
        ? locationsData 
        : locationsData.filter(loc => loc.name === selectedLocation);
      
      // Fetch data for each location and device
      for (const location of locationsToQuery) {
        for (const device of location.devices) {
          // Skip if device type filter is active and doesn't match
          if (selectedDeviceType !== 'all' && device.type !== selectedDeviceType) {
            continue;
          }
          
          // Query all data for this device in the time range
          const data = await queryData({
            start: startISO,
            end: endISO,
            timezone: "UTC",
            filter: {
              location: location.name,
              device_id: device.serialNumber
            }
          });
          
          // Count unique variables in the data
          const uniqueVariables = new Set(data.map(r => r.name));
          const variableCount = uniqueVariables.size;
          
          // Calculate metrics
          const totalRecords = data.length;
          
          // Use actual variable count for expected calculation
          const expectedPoints = calculateExpectedPoints(
            device.type,
            startTime.getTime(),
            endTime.getTime(),
            Math.max(1, variableCount) // At least 1 to avoid division by zero
          );
          
          // Fix completeness calculation - cap at 100%
          const dataCompleteness = expectedPoints > 0 
            ? Math.min(100, (totalRecords / expectedPoints) * 100)
            : 0;
          
          // Get latest timestamp and battery voltage
          let latestTimestamp = null;
          let batteryVoltage = 3.7;
          
          if (data.length > 0) {
            // Sort by timestamp
            const sorted = data.sort((a, b) => 
              new Date(b.timestamp) - new Date(a.timestamp)
            );
            
            latestTimestamp = sorted[0].timestamp;
            
            // Find battery voltage
            const batteryRecord = data.find(r => r.name === 'battery_voltage');
            if (batteryRecord) {
              batteryVoltage = batteryRecord.value;
            }
          }
          
          // Calculate time since last reading relative to END of date range
          let lastSeen = 'Never';
          let status = 'offline';
          
          if (latestTimestamp) {
            const timestampUTC = latestTimestamp.endsWith('Z') ? latestTimestamp : latestTimestamp + 'Z';
            const lastSeenDate = new Date(timestampUTC);
            
            // FIXED: Calculate status relative to END of date range, not current time
            const referenceTime = endTime.getTime();
            const timeSince = (referenceTime - lastSeenDate.getTime()) / 1000 / 60; // minutes
            
            if (timeSince < 60) {
              lastSeen = `${Math.floor(timeSince)} mins ago`;
              status = 'online';
            } else if (timeSince < 24 * 60) {
              const hours = Math.floor(timeSince / 60);
              lastSeen = `${hours} hour${hours > 1 ? 's' : ''} ago`;
              status = timeSince < 2 * 60 ? 'warning' : 'offline';
            } else {
              const days = Math.floor(timeSince / (24 * 60));
              lastSeen = `${days} day${days > 1 ? 's' : ''} ago`;
              status = 'offline';
            }
          }
          
          // Calculate missed submissions - can't be negative
          const missedSubmissions = Math.max(0, expectedPoints - totalRecords);
          
          // Process variables if showVariableStatus is enabled
          let variables = [];
          if (showVariableStatus) {
            const variableData = {};
            data.forEach(record => {
              if (!variableData[record.name]) {
                variableData[record.name] = {
                  name: record.name,
                  recordCount: 0,
                  latestTimestamp: null
                };
              }
              variableData[record.name].recordCount++;
              
              if (!variableData[record.name].latestTimestamp || 
                  new Date(record.timestamp) > new Date(variableData[record.name].latestTimestamp)) {
                variableData[record.name].latestTimestamp = record.timestamp;
              }
            });
            
            // Determine status for each variable
            variables = Object.values(variableData).map(variable => {
              let varStatus = 'offline';
              if (variable.latestTimestamp) {
                const timestampUTC = variable.latestTimestamp.endsWith('Z') 
                  ? variable.latestTimestamp 
                  : variable.latestTimestamp + 'Z';
                const varLastSeenDate = new Date(timestampUTC);
                const varTimeSince = (endTime.getTime() - varLastSeenDate.getTime()) / 1000 / 60;
                
                if (varTimeSince < 60) {
                  varStatus = 'online';
                }
              }
              
              return {
                name: variable.name,
                recordCount: variable.recordCount,
                status: varStatus
              };
            }).sort((a, b) => a.name.localeCompare(b.name));
          }
          
          allDeviceData.push({
            locationName: location.fullName,
            locationAbbrev: location.name,
            deviceName: device.name,
            deviceType: device.type,
            serialNumber: device.serialNumber,
            site: device.site || '-',
            treeSpecies: device.treeSpecies || '-',
            treeDiameter: device.treeDiameter || '-',
            status,
            dataCompleteness,
            totalRecords,
            expectedPoints,
            missedSubmissions,
            lastSeen,
            latestTimestamp,
            batteryVoltage,
            variables: showVariableStatus ? variables : null
          });
        }
      }
      
      setStatusData(allDeviceData);
      setHasGenerated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // No auto-load - user must click Generate button
  
  // Handle quick range selection
  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    
    const end = new Date();
    let start;
    
    if (value === '-1h') {
      start = new Date(end.getTime() - 1 * 60 * 60 * 1000);
    } else if (value === '-12h') {
      start = new Date(end.getTime() - 12 * 60 * 60 * 1000);
    } else if (value === '-24h') {
      start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    } else if (value === '-7d') {
      start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (value === '-30d') {
      start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    
    setDateRange([start, end]);
  };
  
  // Toggle section collapse
  const toggleSection = (locationAbbrev) => {
    setCollapsedSections(prev => ({
      ...prev,
      [locationAbbrev]: !prev[locationAbbrev]
    }));
  };
  
  // Group data by location
  const groupedData = useMemo(() => {
    const grouped = {};
    statusData.forEach(device => {
      if (!grouped[device.locationAbbrev]) {
        grouped[device.locationAbbrev] = {
          locationName: device.locationName,
          locationAbbrev: device.locationAbbrev,
          devices: []
        };
      }
      grouped[device.locationAbbrev].devices.push(device);
    });
    return Object.values(grouped);
  }, [statusData]);
  
  // Calculate summary statistics
  const summary = useMemo(() => {
    const total = statusData.length;
    const online = statusData.filter(d => d.status === 'online').length;
    const offline = statusData.filter(d => d.status === 'offline').length;
    const warning = statusData.filter(d => d.status === 'warning').length;
    const lowCompleteness = statusData.filter(d => d.dataCompleteness < 50).length;
    const needsAttention = statusData.filter(d => 
      d.status === 'offline' || d.dataCompleteness < 50
    );
    
    return { total, online, offline, warning, lowCompleteness, needsAttention };
  }, [statusData]);
  
  const handleCellClick = (device, column) => {
    const params = new URLSearchParams({
      location: device.locationAbbrev,
      device_id: device.serialNumber,
      start: dateRange[0].toISOString(),
      end: dateRange[1].toISOString(),
      timezone: timezone,
      page: '0'
    });
    
    // Open in new tab
    window.open(`/query-browser?${params.toString()}`, '_blank');
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Status Report</h1>
          <p className="text-sm text-gray-600 mt-1">
            Device status and data completeness for selected time range
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel id="quick-range-label" style={{ fontSize: '14px' }}>Quick Range</InputLabel>
            <Select
              labelId="quick-range-label"
              value={quickRange}
              onChange={(e) => handleQuickRangeChange(e.target.value)}
              label="Quick Range"
              style={{ height: '32px', fontSize: '14px' }}
            >
              <MenuItem value="-1h" style={{ fontSize: '14px' }}>Last Hour</MenuItem>
              <MenuItem value="-12h" style={{ fontSize: '14px' }}>Last 12 Hours</MenuItem>
              <MenuItem value="-24h" style={{ fontSize: '14px' }}>Last 24 Hours</MenuItem>
              <MenuItem value="-7d" style={{ fontSize: '14px' }}>Last 7 Days</MenuItem>
              <MenuItem value="-30d" style={{ fontSize: '14px' }}>Last 30 Days</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel id="location-filter-label" style={{ fontSize: '14px' }}>Location</InputLabel>
            <Select
              labelId="location-filter-label"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              label="Location"
              style={{ height: '32px', fontSize: '14px' }}
            >
              <MenuItem value="all" style={{ fontSize: '14px' }}>All Locations</MenuItem>
              {locationsData.map(location => (
                <MenuItem key={location.name} value={location.name} style={{ fontSize: '14px' }}>
                  {location.fullName} ({location.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel id="device-type-filter-label" style={{ fontSize: '14px' }}>Device Type</InputLabel>
            <Select
              labelId="device-type-filter-label"
              value={selectedDeviceType}
              onChange={(e) => setSelectedDeviceType(e.target.value)}
              label="Device Type"
              style={{ height: '32px', fontSize: '14px' }}
            >
              <MenuItem value="all" style={{ fontSize: '14px' }}>All Devices</MenuItem>
              <MenuItem value="MFR Node" style={{ fontSize: '14px' }}>MFR Node</MenuItem>
              <MenuItem value="Sap Flow Sensor" style={{ fontSize: '14px' }}>Sap Flow Meter</MenuItem>
              <MenuItem value="Band Dendrometer" style={{ fontSize: '14px' }}>Band Dendrometer</MenuItem>
              <MenuItem value="WXT536 Sensor" style={{ fontSize: '14px' }}>WXT536 Weather Sensor</MenuItem>
              <MenuItem value="AQT530 Sensor" style={{ fontSize: '14px' }}>AQT530 Air Quality Sensor</MenuItem>
            </Select>
          </FormControl>
          
          <DatetimeRangePicker
            value={dateRange}
            onChange={setDateRange}
          />
          
          <Button
            variant="contained"
            color="primary"
            onClick={fetchStatusData}
            disabled={loading}
            style={{ height: '32px', fontSize: '12px' }}
          >
            {loading ? <CircularProgress size={20} /> : 'Generate'}
          </Button>
          
          <Tooltip title="Export Full Report as HTML">
            <IconButton
              size="small"
              onClick={() => exportFullReportHTML(groupedData, dateRange, summary, timezone, showVariableStatus)}
              disabled={!statusData.length}
            >
              <CloudDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      
      {/* Show Variable Status Checkbox */}
      <div className="mb-4 flex items-center gap-6">
        <FormControlLabel
          control={
            <Checkbox
              checked={showVariableStatus}
              onChange={(e) => setShowVariableStatus(e.target.checked)}
              size="small"
            />
          }
          label={<span className="text-sm">Show variable status for each sensor/node</span>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showDataTimeline}
              onChange={(e) => setShowDataTimeline(e.target.checked)}
              size="small"
            />
          }
          label={<span className="text-sm">Show data timeline for each location</span>}
        />
      </div>
      
      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {loading && (
        <div className="flex items-center justify-center p-12">
          <CircularProgress />
          <span className="ml-3">Generating status report...</span>
        </div>
      )}
      
      {!loading && !hasGenerated && (
        <Alert severity="info" className="mb-4">
          Select your filters and date range, then click <strong>Generate</strong> to create the status report.
        </Alert>
      )}
      
      {!loading && statusData.length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-sm text-gray-600">Total Devices</div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </div>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-sm text-gray-600">Online</div>
              <div className="text-2xl font-bold text-green-600">{summary.online}</div>
            </div>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-sm text-gray-600">Warning</div>
              <div className="text-2xl font-bold text-yellow-600">{summary.warning}</div>
            </div>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-sm text-gray-600">Offline</div>
              <div className="text-2xl font-bold text-red-600">{summary.offline}</div>
            </div>
            <div className="border rounded-lg p-4 bg-white">
              <div className="text-sm text-gray-600">Low Completeness</div>
              <div className="text-2xl font-bold text-red-600">{summary.lowCompleteness}</div>
            </div>
          </div>
          
          {/* Devices Needing Attention */}
          {summary.needsAttention.length > 0 && (
            <div className="border rounded-lg p-4 bg-red-50 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-red-900">
                  Devices Needing Attention ({summary.needsAttention.length})
                </h2>
                <Tooltip title="Export as CSV">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const headers = ['Location', 'Device', 'Type', 'Status', 'Completeness', 'Missed', 'Last Seen'];
                      const rows = summary.needsAttention.map(d => [
                        d.locationName, d.serialNumber, d.deviceType, d.status,
                        d.dataCompleteness.toFixed(1), d.missedSubmissions, d.lastSeen
                      ]);
                      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `crocus-needs-attention-${new Date().toISOString()}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    <CloudDownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Device</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-center">Completeness</th>
                      <th className="px-3 py-2 text-center">Missed</th>
                      <th className="px-3 py-2 text-center">Last Seen</th>
                      <th className="px-3 py-2 text-center">Battery</th>
                      <th className="px-3 py-2 text-center">Data Query</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.needsAttention.map((device, idx) => (
                      <tr key={idx} className="border-b hover:bg-red-100">
                        <td 
                          className="px-3 py-2 cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleCellClick(device)}
                        >
                          {device.locationName}
                        </td>
                        <td 
                          className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleCellClick(device)}
                        >
                          {device.serialNumber}
                        </td>
                        <td 
                          className="px-3 py-2 cursor-pointer text-blue-600 hover:underline"
                          onClick={() => handleCellClick(device)}
                        >
                          {device.deviceType}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <StatusIcon 
                            status={device.status}
                            tooltip={`Status: ${device.status} (at end of date range)`}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <CompletenessBadge 
                            percentage={device.dataCompleteness}
                            tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <HoverCell tooltip="Number of missed data submissions">
                            {device.missedSubmissions}
                          </HoverCell>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                            {device.lastSeen}
                          </HoverCell>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <BatteryIcon batteryVoltage={device.batteryVoltage} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Tooltip 
                            title="Open in Data Query Browser"
                            componentsProps={{ 
                              tooltip: { 
                                sx: { background: '#000' } 
                              } 
                            }}
                          >
                            <IconButton size="small" onClick={() => handleCellClick(device)}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* All Devices by Location - Grouped by Device Type */}
          {groupedData.map((location, idx) => {
            // Group devices by type within this location
            const devicesByType = {
              'MFR Node': [],
              'Sap Flow Sensor': [],
              'Band Dendrometer': [],
              'WXT536 Sensor': [],
              'AQT530 Sensor': []
            };
            
            location.devices.forEach(device => {
              if (devicesByType[device.deviceType]) {
                devicesByType[device.deviceType].push(device);
              }
            });
            
            return (
              <div key={idx} className="border rounded-lg mb-4 overflow-hidden">
                <div 
                  className="bg-purple-900 text-white px-4 py-2 flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection(location.locationAbbrev)}
                >
                  <h2 className="font-semibold">
                    {location.locationName} ({location.locationAbbrev}) - {location.devices.length} devices
                  </h2>
                  <div className="flex items-center gap-2">
                    <Tooltip title="Export Section as CSV">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          exportSectionCSV(location.locationName, location.devices, dateRange, showVariableStatus);
                        }}
                        style={{ color: 'white' }}
                      >
                        <CloudDownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" style={{ color: 'white' }}>
                      {collapsedSections[location.locationAbbrev] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                  </div>
                </div>
                
                {!collapsedSections[location.locationAbbrev] && (
                  <div className="p-4">
                    {/* MFR Nodes Section */}
                    {devicesByType['MFR Node'].length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-semibold mb-2 text-purple-800">MFR Nodes</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Serial</th>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-center">Completeness</th>
                                <th className="px-3 py-2 text-center">Records</th>
                                <th className="px-3 py-2 text-center">Expected</th>
                                <th className="px-3 py-2 text-center">Missed</th>
                                <th className="px-3 py-2 text-center">Last Seen</th>
                                <th className="px-3 py-2 text-center">Battery</th>
                                <th className="px-3 py-2 text-center">Data Query</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicesByType['MFR Node'].map((device, deviceIdx) => (
                                <React.Fragment key={deviceIdx}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td 
                                      className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                                      onClick={() => handleCellClick(device)}
                                    >
                                      {device.serialNumber}
                                    </td>
                                    <td className="px-3 py-2">{device.site}</td>
                                    <td className="px-3 py-2 text-center">
                                      <StatusIcon 
                                        status={device.status}
                                        tooltip={`Status: ${device.status} (at end of date range)`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <CompletenessBadge 
                                        percentage={device.dataCompleteness}
                                        tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">{device.totalRecords}</td>
                                    <td className="px-3 py-2 text-center">{device.expectedPoints}</td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip="Number of missed data submissions">
                                        {device.missedSubmissions}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                                        {device.lastSeen}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <BatteryIcon batteryVoltage={device.batteryVoltage} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Tooltip 
                                        title="Open in Data Query Browser"
                                        componentsProps={{ 
                                          tooltip: { 
                                            sx: { background: '#000' } 
                                          } 
                                        }}
                                      >
                                        <IconButton size="small" onClick={() => handleCellClick(device)}>
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                  
                                  {/* Variable Status Rows */}
                                  {showVariableStatus && device.variables && device.variables.map((variable, varIdx) => (
                                    <tr key={`var-${deviceIdx}-${varIdx}`} className="bg-gray-50 text-xs">
                                      <td className="px-3 py-2 pl-8" colSpan="2">
                                        <span className="text-gray-600">└─</span> {variable.name}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <StatusIcon 
                                          status={variable.status}
                                          tooltip={`Variable status: ${variable.status}`}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-center" colSpan="6">
                                        <HoverCell tooltip={`Total records for ${variable.name}`}>
                                          {variable.recordCount} records
                                        </HoverCell>
                                      </td>
                                      <td className="px-3 py-2 text-center"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Sap Flow Sensors Section */}
                    {devicesByType['Sap Flow Sensor'].length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-semibold mb-2 text-purple-800">Sap Flow Sensors</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Serial</th>
                                <th className="px-3 py-2 text-left">Species</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-center">Completeness</th>
                                <th className="px-3 py-2 text-center">Records</th>
                                <th className="px-3 py-2 text-center">Expected</th>
                                <th className="px-3 py-2 text-center">Missed</th>
                                <th className="px-3 py-2 text-center">Last Seen</th>
                                <th className="px-3 py-2 text-center">Battery</th>
                                <th className="px-3 py-2 text-center">Data Query</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicesByType['Sap Flow Sensor'].map((device, deviceIdx) => (
                                <React.Fragment key={deviceIdx}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td 
                                      className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                                      onClick={() => handleCellClick(device)}
                                    >
                                      {device.serialNumber}
                                    </td>
                                    <td className="px-3 py-2">{device.treeSpecies}</td>
                                    <td className="px-3 py-2 text-center">
                                      <StatusIcon 
                                        status={device.status}
                                        tooltip={`Status: ${device.status} (at end of date range)`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <CompletenessBadge 
                                        percentage={device.dataCompleteness}
                                        tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">{device.totalRecords}</td>
                                    <td className="px-3 py-2 text-center">{device.expectedPoints}</td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip="Number of missed data submissions">
                                        {device.missedSubmissions}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                                        {device.lastSeen}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <BatteryIcon batteryVoltage={device.batteryVoltage} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Tooltip 
                                        title="Open in Data Query Browser"
                                        componentsProps={{ 
                                          tooltip: { 
                                            sx: { background: '#000' } 
                                          } 
                                        }}
                                      >
                                        <IconButton size="small" onClick={() => handleCellClick(device)}>
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                  
                                  {/* Variable Status Rows */}
                                  {showVariableStatus && device.variables && device.variables.map((variable, varIdx) => (
                                    <tr key={`var-${deviceIdx}-${varIdx}`} className="bg-gray-50 text-xs">
                                      <td className="px-3 py-2 pl-8" colSpan="2">
                                        <span className="text-gray-600">└─</span> {variable.name}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <StatusIcon 
                                          status={variable.status}
                                          tooltip={`Variable status: ${variable.status}`}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-center" colSpan="6">
                                        <HoverCell tooltip={`Total records for ${variable.name}`}>
                                          {variable.recordCount} records
                                        </HoverCell>
                                      </td>
                                      <td className="px-3 py-2 text-center"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Band Dendrometers Section */}
                    {devicesByType['Band Dendrometer'].length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-semibold mb-2 text-purple-800">Band Dendrometers</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Serial</th>
                                <th className="px-3 py-2 text-left">Species</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-center">Completeness</th>
                                <th className="px-3 py-2 text-center">Records</th>
                                <th className="px-3 py-2 text-center">Expected</th>
                                <th className="px-3 py-2 text-center">Missed</th>
                                <th className="px-3 py-2 text-center">Last Seen</th>
                                <th className="px-3 py-2 text-center">Battery</th>
                                <th className="px-3 py-2 text-center">Data Query</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicesByType['Band Dendrometer'].map((device, deviceIdx) => (
                                <React.Fragment key={deviceIdx}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td 
                                      className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                                      onClick={() => handleCellClick(device)}
                                    >
                                      {device.serialNumber}
                                    </td>
                                    <td className="px-3 py-2">{device.treeSpecies}</td>
                                    <td className="px-3 py-2 text-center">
                                      <StatusIcon 
                                        status={device.status}
                                        tooltip={`Status: ${device.status} (at end of date range)`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <CompletenessBadge 
                                        percentage={device.dataCompleteness}
                                        tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">{device.totalRecords}</td>
                                    <td className="px-3 py-2 text-center">{device.expectedPoints}</td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip="Number of missed data submissions">
                                        {device.missedSubmissions}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                                        {device.lastSeen}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <BatteryIcon batteryVoltage={device.batteryVoltage} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Tooltip 
                                        title="Open in Data Query Browser"
                                        componentsProps={{ 
                                          tooltip: { 
                                            sx: { background: '#000' } 
                                          } 
                                        }}
                                      >
                                        <IconButton size="small" onClick={() => handleCellClick(device)}>
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                  
                                  {/* Variable Status Rows */}
                                  {showVariableStatus && device.variables && device.variables.map((variable, varIdx) => (
                                    <tr key={`var-${deviceIdx}-${varIdx}`} className="bg-gray-50 text-xs">
                                      <td className="px-3 py-2 pl-8" colSpan="2">
                                        <span className="text-gray-600">└─</span> {variable.name}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <StatusIcon 
                                          status={variable.status}
                                          tooltip={`Variable status: ${variable.status}`}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-center" colSpan="6">
                                        <HoverCell tooltip={`Total records for ${variable.name}`}>
                                          {variable.recordCount} records
                                        </HoverCell>
                                      </td>
                                      <td className="px-3 py-2 text-center"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* WXT536 Weather Sensors Section */}
                    {devicesByType['WXT536 Sensor'].length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-semibold mb-2 text-purple-800">WXT536 Weather Sensors</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Device ID</th>
                                <th className="px-3 py-2 text-left">Site</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-center">Completeness</th>
                                <th className="px-3 py-2 text-center">Records</th>
                                <th className="px-3 py-2 text-center">Expected</th>
                                <th className="px-3 py-2 text-center">Missed</th>
                                <th className="px-3 py-2 text-center">Last Seen</th>
                                <th className="px-3 py-2 text-center">Battery</th>
                                <th className="px-3 py-2 text-center">Data Query</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicesByType['WXT536 Sensor'].map((device, deviceIdx) => (
                                <React.Fragment key={deviceIdx}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td 
                                      className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                                      onClick={() => handleCellClick(device)}
                                    >
                                      {device.serialNumber}
                                    </td>
                                    <td className="px-3 py-2">{device.site}</td>
                                    <td className="px-3 py-2 text-center">
                                      <StatusIcon 
                                        status={device.status}
                                        tooltip={`Status: ${device.status} (at end of date range)`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <CompletenessBadge 
                                        percentage={device.dataCompleteness}
                                        tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">{device.totalRecords}</td>
                                    <td className="px-3 py-2 text-center">{device.expectedPoints}</td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip="Number of missed data submissions">
                                        {device.missedSubmissions}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                                        {device.lastSeen}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <BatteryIcon batteryVoltage={device.batteryVoltage} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Tooltip 
                                        title="Open in Data Query Browser"
                                        componentsProps={{ tooltip: { sx: { background: '#000' } } }}
                                      >
                                        <IconButton size="small" onClick={() => handleCellClick(device)}>
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                  {showVariableStatus && device.variables && device.variables.map((variable, varIdx) => (
                                    <tr key={`var-${deviceIdx}-${varIdx}`} className="bg-gray-50 text-xs">
                                      <td className="px-3 py-2 pl-8" colSpan="2">
                                        <span className="text-gray-600">└─</span> {variable.name}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <StatusIcon status={variable.status} tooltip={`Variable status: ${variable.status}`} />
                                      </td>
                                      <td className="px-3 py-2 text-center" colSpan="6">
                                        <HoverCell tooltip={`Total records for ${variable.name}`}>{variable.recordCount} records</HoverCell>
                                      </td>
                                      <td className="px-3 py-2 text-center"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* AQT530 Air Quality Sensors Section */}
                    {devicesByType['AQT530 Sensor'].length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-semibold mb-2 text-purple-800">AQT530 Air Quality Sensors</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm border">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Device ID</th>
                                <th className="px-3 py-2 text-left">Site</th>
                                <th className="px-3 py-2 text-center">Status</th>
                                <th className="px-3 py-2 text-center">Completeness</th>
                                <th className="px-3 py-2 text-center">Records</th>
                                <th className="px-3 py-2 text-center">Expected</th>
                                <th className="px-3 py-2 text-center">Missed</th>
                                <th className="px-3 py-2 text-center">Last Seen</th>
                                <th className="px-3 py-2 text-center">Battery</th>
                                <th className="px-3 py-2 text-center">Data Query</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicesByType['AQT530 Sensor'].map((device, deviceIdx) => (
                                <React.Fragment key={deviceIdx}>
                                  <tr className="border-b hover:bg-gray-50">
                                    <td 
                                      className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-600 hover:underline"
                                      onClick={() => handleCellClick(device)}
                                    >
                                      {device.serialNumber}
                                    </td>
                                    <td className="px-3 py-2">{device.site}</td>
                                    <td className="px-3 py-2 text-center">
                                      <StatusIcon 
                                        status={device.status}
                                        tooltip={`Status: ${device.status} (at end of date range)`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <CompletenessBadge 
                                        percentage={device.dataCompleteness}
                                        tooltip={`Data completeness: ${device.totalRecords} of ${device.expectedPoints} expected records`}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">{device.totalRecords}</td>
                                    <td className="px-3 py-2 text-center">{device.expectedPoints}</td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip="Number of missed data submissions">
                                        {device.missedSubmissions}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <HoverCell tooltip={`Last data received: ${device.latestTimestamp || 'Never'}`}>
                                        {device.lastSeen}
                                      </HoverCell>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <BatteryIcon batteryVoltage={device.batteryVoltage} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <Tooltip 
                                        title="Open in Data Query Browser"
                                        componentsProps={{ tooltip: { sx: { background: '#000' } } }}
                                      >
                                        <IconButton size="small" onClick={() => handleCellClick(device)}>
                                          <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </td>
                                  </tr>
                                  {showVariableStatus && device.variables && device.variables.map((variable, varIdx) => (
                                    <tr key={`var-${deviceIdx}-${varIdx}`} className="bg-gray-50 text-xs">
                                      <td className="px-3 py-2 pl-8" colSpan="2">
                                        <span className="text-gray-600">└─</span> {variable.name}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <StatusIcon status={variable.status} tooltip={`Variable status: ${variable.status}`} />
                                      </td>
                                      <td className="px-3 py-2 text-center" colSpan="6">
                                        <HoverCell tooltip={`Total records for ${variable.name}`}>{variable.recordCount} records</HoverCell>
                                      </td>
                                      <td className="px-3 py-2 text-center"></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Data Timeline Section */}
                    {showDataTimeline && (
                      <StatusTimeline 
                        location={locationsData.find(loc => loc.name === location.locationAbbrev)}
                        dateRange={dateRange}
                        showVariables={showVariableStatus}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      
      {!loading && hasGenerated && statusData.length === 0 && selectedLocation !== 'all' && (
        <Alert severity="info">
          No data available for the selected location and time range.
        </Alert>
      )}
      
      {!loading && hasGenerated && statusData.length === 0 && selectedLocation === 'all' && (
        <Alert severity="info">
          No data available for the selected time range.
        </Alert>
      )}
    </div>
  );
}